import React from "react";
import { ExclamationIcon, XIcon } from "@heroicons/react/outline";

/**
 * Modal for confirming removal of all lineup selections
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Function} props.onConfirm - Callback when removal is confirmed
 * @param {Array} props.lineup - Array of selected fighters
 * @param {Array} props.alternates - Array of alternate fighters
 * @returns {JSX.Element}
 */
const RemoveAllConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  lineup,
  alternates,
}) => {
  if (!isOpen) return null;

  // Count selected fighters
  const selectedFighters = lineup.filter((fighter) => fighter).length;
  const selectedAlternates = alternates.length;
  const totalSelections = selectedFighters + selectedAlternates;

  return (
    <div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center p-2 sm:p-4">
      <div
        className="fixed inset-0 bg-dark-grey bg-opacity-75 transition-opacity"
        onClick={onClose}
      ></div>

      <div className="relative transform overflow-hidden rounded-t-2xl sm:rounded-xl bg-white text-left shadow-2xl transition-all w-full max-w-sm sm:max-w-md mx-auto max-h-[90vh] sm:max-h-[85vh] flex flex-col mb-safe">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-red-500 text-white p-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <ExclamationIcon className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">
                  Remove All Selections
                </h3>
                <p className="text-white/80 text-sm">
                  This action cannot be undone
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors text-white"
            >
              <XIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-4">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ExclamationIcon className="w-8 h-8 text-red-600" />
            </div>
            <h4 className="text-lg font-semibold text-dark-grey mb-2">
              Are you sure you want to remove all selections?
            </h4>
            <p className="text-medium-grey text-sm leading-relaxed">
              This will clear your entire lineup and you'll need to start over.
              All your current selections will be lost.
            </p>
          </div>

          {/* Selection Summary */}
          {totalSelections > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
              <h5 className="font-semibold text-sm text-dark-grey mb-3">
                What will be removed:
              </h5>
              <div className="space-y-2">
                {selectedFighters > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-medium-grey">
                      Primary lineup fighters
                    </span>
                    <span className="text-sm font-semibold text-red-600">
                      {selectedFighters}
                    </span>
                  </div>
                )}
                {selectedAlternates > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-medium-grey">
                      Alternate fighters
                    </span>
                    <span className="text-sm font-semibold text-red-600">
                      {selectedAlternates}
                    </span>
                  </div>
                )}
                <div className="border-t border-red-200 pt-2 mt-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-dark-grey">
                      Total selections
                    </span>
                    <span className="text-sm font-bold text-red-600">
                      {totalSelections}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Fixed Actions Footer */}
        <div className="p-4 bg-light-grey border-t border-gray-200 flex-shrink-0">
          <div className="space-y-3">
            <button
              type="button"
              onClick={onConfirm}
              className="w-full bg-red-600 hover:bg-red-700 text-white py-4 px-4 rounded-xl text-base font-bold transition-colors min-h-[48px] flex items-center justify-center shadow-sm"
            >
              Yes, Remove All
            </button>
            <button
              type="button"
              onClick={onClose}
              className="w-full bg-white text-dark-grey py-3 px-4 rounded-xl text-sm font-medium border border-light-grey hover:bg-gray-50 transition-colors min-h-[44px] flex items-center justify-center"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RemoveAllConfirmationModal;
